import type { BlogPost } from '@/types/blog';

const SITE_URL = 'https://linkedbysix.com';

export interface BlogSeoQaIssue {
  code: string;
  message: string;
}

export interface BlogSeoQaMetrics {
  metaDescriptionLength: number;
  internalLinkCount: number;
  faqSectionDetected: boolean;
}

export interface BlogSeoQaResult {
  passes: boolean;
  issues: BlogSeoQaIssue[];
  metrics: BlogSeoQaMetrics;
}

type BlogSeoQaInput = Partial<
  Pick<
    BlogPost,
    | 'title'
    | 'slug'
    | 'content'
    | 'meta_description'
    | 'canonical_url'
    | 'primary_keyword'
    | 'featured_image_url'
    | 'featured_image_alt'
    | 'allow_publish_without_image'
    | 'structured_data'
    | 'content_json'
  >
>;

function pushIssue(issues: BlogSeoQaIssue[], code: string, message: string): void {
  issues.push({ code, message });
}

function normalizeCanonicalUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function containsTerm(source: string, term: string): boolean {
  return source.toLowerCase().includes(term.toLowerCase());
}

function stripHtml(html: string): string {
  return html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractFirstParagraphText(content: string): string {
  const firstParagraphMatch = content.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  if (firstParagraphMatch?.[1]) {
    return stripHtml(firstParagraphMatch[1]);
  }
  return stripHtml(content).slice(0, 280);
}

function countInternalLinks(content: string): number {
  const anchorRegex = /<a\s+[^>]*href=(?:"([^"]+)"|'([^']+)')[^>]*>/gi;
  let count = 0;
  let match: RegExpExecArray | null = null;

  while ((match = anchorRegex.exec(content)) !== null) {
    const href = (match[1] || match[2] || '').trim();
    if (!href) continue;

    const isInternalRelative = href.startsWith('/');
    const isInternalAbsolute = href.startsWith(`${SITE_URL}/`);
    if (isInternalRelative || isInternalAbsolute) {
      count += 1;
    }
  }

  return count;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asSchemaObjects(structuredData: BlogPost['structured_data']): Record<string, unknown>[] {
  if (!structuredData) return [];

  if (Array.isArray(structuredData)) {
    return structuredData.filter(isRecord);
  }

  if (!isRecord(structuredData)) {
    return [];
  }

  const graph = structuredData['@graph'];
  if (Array.isArray(graph)) {
    return graph.filter(isRecord);
  }

  return [structuredData];
}

function isFaqPageSchema(schema: Record<string, unknown>): boolean {
  const typeValue = schema['@type'];
  if (typeof typeValue === 'string') {
    return typeValue === 'FAQPage';
  }
  if (Array.isArray(typeValue)) {
    return typeValue.includes('FAQPage');
  }
  return false;
}

function countValidFaqEntries(faqSchema: Record<string, unknown>): number {
  const mainEntity = faqSchema.mainEntity;
  if (!Array.isArray(mainEntity)) return 0;

  let validEntries = 0;
  for (const item of mainEntity) {
    if (!isRecord(item)) continue;

    const question = item.name;
    const acceptedAnswer = item.acceptedAnswer;
    if (typeof question !== 'string' || !question.trim()) continue;
    if (!isRecord(acceptedAnswer)) continue;
    if (typeof acceptedAnswer.text !== 'string' || !acceptedAnswer.text.trim()) continue;

    validEntries += 1;
  }

  return validEntries;
}

function hasFaqSectionInContent(content: string, contentJson: BlogPost['content_json']): boolean {
  if (/<h[2-4][^>]*>\s*(faq|frequently asked questions?)/i.test(content)) {
    return true;
  }

  if (!Array.isArray(contentJson)) {
    return false;
  }

  return contentJson.some((section) => section?.type === 'faq');
}

export function validateBlogSeoQa(post: BlogSeoQaInput): BlogSeoQaResult {
  const issues: BlogSeoQaIssue[] = [];

  const title = (post.title || '').trim();
  const slug = (post.slug || '').trim();
  const content = (post.content || '').trim();
  const metaDescription = (post.meta_description || '').trim();
  const canonicalUrl = (post.canonical_url || '').trim();
  const primaryKeyword = (post.primary_keyword || '').trim();
  const allowPublishWithoutImage = !!post.allow_publish_without_image;
  const hasImage = !!post.featured_image_url?.trim();
  const hasAlt = !!post.featured_image_alt?.trim();
  const expectedCanonical = slug ? `${SITE_URL}/blog/${slug}` : '';
  const canonicalForValidation = canonicalUrl || expectedCanonical;

  if (!title) {
    pushIssue(issues, 'title_missing', 'Post title is required.');
  } else if (title.length < 35) {
    pushIssue(issues, 'title_too_short', 'Title should be at least 35 characters for stronger search intent matching.');
  }

  if (!slug) {
    pushIssue(issues, 'slug_missing', 'URL slug is required.');
  }

  if (!content) {
    pushIssue(issues, 'content_missing', 'Post content is required.');
  }

  if (!metaDescription) {
    pushIssue(issues, 'meta_description_missing', 'Meta description is required.');
  } else if (metaDescription.length < 120 || metaDescription.length > 160) {
    pushIssue(
      issues,
      'meta_description_length',
      'Meta description must be between 120 and 160 characters.'
    );
  }

  if (!canonicalForValidation) {
    pushIssue(issues, 'canonical_missing', 'Canonical URL is required before publishing.');
  } else if (
    expectedCanonical &&
    normalizeCanonicalUrl(canonicalForValidation) !== normalizeCanonicalUrl(expectedCanonical)
  ) {
    pushIssue(
      issues,
      'canonical_mismatch',
      `Canonical URL must match ${expectedCanonical}.`
    );
  }

  if (!primaryKeyword) {
    pushIssue(issues, 'primary_keyword_missing', 'Primary keyword is required.');
  } else {
    if (!containsTerm(title, primaryKeyword)) {
      pushIssue(issues, 'keyword_not_in_title', 'Primary keyword must appear in the title.');
    }

    const introText = extractFirstParagraphText(content);
    if (!containsTerm(introText, primaryKeyword)) {
      pushIssue(
        issues,
        'keyword_not_in_intro',
        'Primary keyword must appear in the opening paragraph or intro copy.'
      );
    }
  }

  const internalLinkCount = countInternalLinks(content);
  if (internalLinkCount < 2) {
    pushIssue(
      issues,
      'internal_links_low',
      'Add at least 2 internal links to related pages or articles.'
    );
  }

  if (!allowPublishWithoutImage) {
    if (!hasImage) {
      pushIssue(issues, 'featured_image_missing', 'Featured image is required unless publish override is enabled.');
    }
    if (hasImage && !hasAlt) {
      pushIssue(issues, 'featured_image_alt_missing', 'Alt text is required for the featured image.');
    }
  }

  const faqSectionDetected = hasFaqSectionInContent(content, post.content_json || null);
  if (faqSectionDetected) {
    const faqSchema = asSchemaObjects(post.structured_data || null).find(isFaqPageSchema);

    if (!faqSchema) {
      pushIssue(
        issues,
        'faq_schema_missing',
        'FAQ section detected in content, but FAQPage schema is missing.'
      );
    } else if (countValidFaqEntries(faqSchema) < 2) {
      pushIssue(
        issues,
        'faq_schema_invalid',
        'FAQPage schema must contain at least 2 valid question/answer pairs.'
      );
    }
  }

  return {
    passes: issues.length === 0,
    issues,
    metrics: {
      metaDescriptionLength: metaDescription.length,
      internalLinkCount,
      faqSectionDetected,
    },
  };
}
